import React, { useState } from 'react';
import { useScrollLock } from '@hooks';
import { Plus, Trash2, Edit } from 'lucide-react';
import { Icon } from '../../../icons';
import type { EquipmentCategory, EquipmentCategoryCreate, EquipmentCategoryUpdate } from '../../../types/equipmentCategory';
import type { Equipment } from '../../../types/equipment';
import { CategoryForm } from '@components';

type CategoriesTabProps = {
  categories: EquipmentCategory[];
  equipments: Equipment[];
  onAddCategory: (data: EquipmentCategoryCreate) => void;
  onEditCategory: (id: string, data: EquipmentCategoryUpdate) => void;
  onDeleteCategory: (categoryId: string) => void;
};

export const CategoriesTab: React.FC<CategoriesTabProps> = ({
  categories,
  equipments,
  onAddCategory,
  onEditCategory,
  onDeleteCategory
}) => {
  const [showForm, setShowForm] = useState(false);
  useScrollLock(showForm);
  const [editCategory, setEditCategory] = useState<EquipmentCategory | null>(null);

  const getEquipmentCount = (categoryId: string) => {
    return equipments.filter(eq => eq.categoryId === categoryId).length;
  };

  const handleAddClick = () => {
    setEditCategory(null);
    setShowForm(true);
  };

  const handleEditClick = (category: EquipmentCategory) => {
    setEditCategory(category);
    setShowForm(true);
  };

  const handleFormSubmit = (data: EquipmentCategoryCreate | EquipmentCategoryUpdate) => {
    if (editCategory) {
      onEditCategory(editCategory.id, data);
    } else {
      onAddCategory(data as EquipmentCategoryCreate);
    }
    setShowForm(false);
    setEditCategory(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditCategory(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Categories Management</h2>
        <button
          onClick={handleAddClick}
          className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} className="mr-2" />
          Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {categories.map((category) => {
          const equipmentCount = getEquipmentCount(category.id);
          return (
            <div key={category.id} className="p-6 bg-white rounded-lg shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Icon name={category.icon ?? 'default'} size={22} title={category.name} className="mr-2" />
                    {category.name}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">{category.description}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    className="text-yellow-600 hover:text-yellow-900 cursor-pointer"
                    onClick={() => handleEditClick(category)}
                    title="Edit category"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-900"
                    onClick={() => onDeleteCategory(category.id)}
                    title="Delete category"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-start space-x-2">
                <span className="text-sm text-gray-500">Cantidad de equipos:</span>
                <span className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                  {equipmentCount}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <CategoryForm
          initialData={editCategory ?? undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isEdit={!!editCategory}
        />
      )}
    </div>
  );
};

